import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, X } from "lucide-react";

/**
 * FirstLoginTaskNudge
 * -------------------
 * A day-one prompt that fires the very first time a brand-new user lands on
 * their dashboard.  Nudges them to add their first task so they experience
 * the core loop of the app immediately, instead of staring at an empty
 * board.  Tracked per-user in localStorage under `aii-task-nudge:{userId}`.
 *
 * Deliberately delayed 4.5s after mount so it doesn't fight with the
 * FirstLoginNotifPrompt (which fires at 1.2s) — the notif ask always comes
 * first, this second-tier nudge second.
 */
export default function FirstLoginTaskNudge({ userId }) {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!userId) return;
    const key = `aii-task-nudge:${userId}`;
    if (localStorage.getItem(key) === "1") return;
    // Give the first-login notif prompt some breathing room.
    const t = setTimeout(() => setOpen(true), 4500);
    return () => clearTimeout(t);
  }, [userId]);

  const dismiss = () => {
    if (userId) localStorage.setItem(`aii-task-nudge:${userId}`, "1");
    setOpen(false);
  };

  const openTasks = () => {
    dismiss();
    nav("/my-day");
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[65] flex items-center justify-center p-4"
         data-testid="first-login-task-nudge" onClick={dismiss}>
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[color:var(--aii-green-tint)] flex items-center justify-center flex-shrink-0">
              <ClipboardCheck className="text-brand" size={20}/>
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold">Add your first task?</h3>
              <p className="text-xs text-[color:var(--aii-muted)] mt-0.5">Kickstart your day-one loop · 30 seconds</p>
            </div>
          </div>
          <button onClick={dismiss} className="text-[color:var(--aii-muted)] hover:text-red-600 p-1"><X size={16}/></button>
        </div>
        <div className="p-5 text-sm text-[color:var(--aii-text-2)] space-y-2">
          <p>Aii Group runs on tasks — arrivals, C-Forms, partner follow-ups all show up as one-line to-dos.  The fastest way to feel the app click is to add one you already have on your mind.</p>
          <p className="text-[color:var(--aii-muted)] text-xs">You can always come back — this nudge only fires once.</p>
        </div>
        <div className="p-4 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
          <button data-testid="first-login-task-skip" onClick={dismiss}
                  className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50 font-semibold">
            Maybe later
          </button>
          <button data-testid="first-login-task-open" onClick={openTasks}
                  className="px-4 py-2 text-sm bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5">
            <ClipboardCheck size={13}/> Open My Day
          </button>
        </div>
      </div>
    </div>
  );
}
